/** 6 -> 7: every stack in a saved battle carries a magic-strike budget.
 *
 * `MAGIC_ATTACK` gave `StackStore` a column of its own - `strikes`, the charged
 * blows a stack has left. A battle saved before this has every other column and
 * not that one, and `shapes.ts` refuses a store that is missing a declared key
 * rather than defaulting it, which is the rule that makes a forgotten migration
 * a loud failure instead of a silent zero somewhere in the middle of a fight.
 *
 * The rung writes a zero for every stack, and zero is the honest value: no
 * creature in the bundle those saves were written against had the ability at
 * all, so no stack in one of them had ever banked a charged blow. A stack that
 * COULD have one is re-budgeted the next time a battle opens (`init.ts` reads
 * `strikesForCreature`); this rung only has to make the saved battle loadable.
 *
 * The length comes from the store's own `n` rather than from a sibling column,
 * for the same reason `0006` sized the ground plane from the map header: a file
 * whose columns disagree with its own header is malformed, and saying so is the
 * validator's job after this rung, not something to paper over here.
 */

import type { Migration } from "./types.js";
import { isRecord, readSave, writeSave } from "./tree.js";

/** How many stacks the saved store says it holds. */
function stackCount(store: Record<string, unknown>): number {
  const n = store["n"];
  if (typeof n === "number" && Number.isInteger(n) && n >= 0) return n;
  // No usable `n`: fall back to the width of a column that has always existed,
  // so a store written by an older dev build still lands on the right length.
  const casts = store["casts"];
  return Array.isArray(casts) ? casts.length : 0;
}

export const migration0007: Migration = {
  from: 6,
  to: 7,
  describe: "give every stack in a saved battle a magic-strike budget",
  migrate(raw: unknown): Record<string, unknown> | null {
    const save = readSave(raw);
    if (save === null) return null;

    const battle = save.state["battle"];
    // No battle in the save is the ordinary case - most saves are made on the
    // adventure map - and there is nothing to add to.
    if (!isRecord(battle)) return writeSave(save, save.state, 7);

    const stacks = battle["stacks"];
    if (!isRecord(stacks)) return writeSave(save, save.state, 7);
    if (Object.prototype.hasOwnProperty.call(stacks, "strikes")) {
      return writeSave(save, save.state, 7);
    }

    const next = {
      ...save.state,
      battle: {
        ...battle,
        stacks: { ...stacks, strikes: new Array<number>(stackCount(stacks)).fill(0) },
      },
    };
    return writeSave(save, next, 7);
  },
};
